import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dynamic from 'next/dynamic';
import Media from 'react-media';
import {
  Badge,
  Box,
  Button,
  DropdownButton,
  FilterBar,
  FilterBarDateRangePicker,
  FilterBarSeparator,
  FilterBarTab,
  Menu,
  MenuItem,
  Table,
  TableCell,
  TableHeader,
  TableHeaders,
  TableRow,
  Text,
  TimeField,
  View,
} from './ui';
import type {
  Order,
  RelatedOrgsQuery,
  ScenarioTimeline_CoaFragment,
  ScenarioTimeline_OrganizationFragment,
  ScenarioTimeline_PortCallFragment,
} from '../graphql';
import {
  useVoyagesTable_BillOfLadingsQuery,
  useVoyagesTable_MeasurementReportsQuery,
} from '../graphql';
import type { TableRowProps } from './ui/Table/Table';
import { NotFoundTableRow } from './ui/Table/Table';
import type { Column, Row } from 'react-table';
import {
  useExpanded,
  useFilters,
  useGlobalFilter,
  useSortBy,
  useTable,
} from 'react-table';
import { getEventLocation } from './core/Core';
import type { ScheduleDataContext } from './core/ScheduleDataContext';
import type { CargoEvent, PortCallEvent, TimeData } from './core/TimeData';
import {
  CoreEventType,
  PortCallEventType,
  isPortCallEventTimeData,
} from './core/TimeData';
import { exhaustiveCheck, isDefined, mapKeyBy, typedKeyBy } from '../lib/util';
import type {
  EventTimeline,
  TimelineFocus,
  TimelineVoyage,
} from './core/Timelines';
import { getVoyages, getVoyageStatus, VoyageStatus } from './core/Timelines';
import { getOrderDisplayName, parseAmountInfo } from './order/order';
import { useLocalSetting } from './hooks/useLocalConfig';
import { Resizable } from 're-resizable';
import type { GlobalFilter } from '../pages/orders';
import { SearchFilter, TableColumnsSelector } from '../pages/orders';
import { getMassUnitName, getVolumeUnitName } from '../constants/capacityUnits';
import { uniqBy } from 'lodash';
import { getPortCallTimeSourceType } from './PortCallUpdates';
import { hasUserRight } from '../state/zustand';
import LoadingSpinner from './LoadingSpinner';
import { mapGroupBy } from '@seaber/common/lib/collections';

type VoyagesTablePortCall = ScenarioTimeline_PortCallFragment;
type VoyagesTableOrganization = ScenarioTimeline_OrganizationFragment;
type VoyagesTableCoaContract = ScenarioTimeline_CoaFragment;

type VoyagesTableProps = {
  timelines: EventTimeline[];
  coaContracts: ReadonlyArray<VoyagesTableCoaContract>;
  scheduleContext: Pick<
    ScheduleDataContext,
    'vessels' | 'locations' | 'portCalls'
  >;
  organizations: Map<number, VoyagesTableOrganization>;
  onViewOrder: (orderId: Order['id']) => void;
  onViewAssignment: (timelineFocus?: TimelineFocus) => void;
  relatedOrgsData?: RelatedOrgsQuery | undefined;
};

type VoyagesTableRowProps = {
  row: Row<VoyageTableRowData>;
  onSelect: (
    event: React.MouseEvent<HTMLTableRowElement, MouseEvent>,
    row: Row<VoyageTableRowData>,
    index: number,
  ) => void;
  onDblClick: (
    event: React.MouseEvent<HTMLTableRowElement, MouseEvent>,
    row: Row<VoyageTableRowData>,
  ) => void;
  hiddenCells: string[];
  index: number;
};

const VoyagesTableRow = ({
  row,
  onSelect,
  onDblClick,
  index,
  hiddenCells,
  ...restProps
}: VoyagesTableRowProps & TableRowProps): JSX.Element => {
  return (
    <TableRow
      onClick={(event) => onSelect(event, row, index)}
      onDoubleClick={
        row.original.isReadOnly ? undefined : (event) => onDblClick(event, row)
      }
      {...restProps}
    >
      {row.cells.map((cell) => {
        const columnId = cell.column.id;
        const cellProps = cell.getCellProps();
        const cellValue = cell.render('Cell');
        return (
          <TableCell {...cellProps} key={cellProps.key}>
            {hiddenCells.includes(columnId) ? '' : cellValue}
          </TableCell>
        );
      })}
    </TableRow>
  );
};

type VoyageTableRowData = {
  voyage: TimelineVoyage;
  filterKeywords: string[];
  shipownerName: string | null;
  event: CargoEvent;
  portcall: VoyagesTablePortCall | null;
  allCargoEvents: TimeData<CargoEvent>[];
  allPortCallEvents: {
    [K in PortCallEventType]?: TimeData<PortCallEvent> | null;
  };
  subRows: VoyageTableRowData[];
  expanded: true;
  isReadOnly: boolean;
};

const portCallTimeCellContent = (
  timetype: PortCallEventType,
  value: number | null,
  row: VoyageTableRowData,
) => {
  if (!row.portcall)
    return <span style={{ color: 'orange' }}>NO PORT CALL</span>;

  const sourceType = getPortCallTimeSourceType(row.portcall, timetype);
  const isActual = sourceType === 'actual';
  const isPast = Date.now() - (value ?? 0) > 0;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
      }}
    >
      <Badge
        isRounded
        variant={
          isActual ? 'portcalltimestatus/actual' : isPast ? 'past' : undefined
        }
        marginRight={2}
      >
        {sourceType === 'actual'
          ? 'A'
          : sourceType === 'estimate'
            ? 'E'
            : sourceType === 'planned'
              ? 'P'
              : sourceType === 'requested'
                ? 'R'
                : exhaustiveCheck(sourceType)}
      </Badge>
      <TimeField
        value={value}
        timeZone={row.event.locationTimeZone ?? undefined}
      />
    </div>
  );
};

const VoyageFilterTab: (props: {
  label: string;
  voyagesByStatus: number;
  currentValue: VoyageStatus | null;
  tabValue: VoyageStatus | null;
  setActive: (x: VoyageStatus | null) => void;
}) => JSX.Element = ({
  label,
  voyagesByStatus,
  currentValue,
  tabValue,
  setActive,
}) => {
  return (
    <FilterBarTab
      onPress={() => setActive(tabValue)}
      isActive={currentValue === tabValue}
    >
      <Text mr={3}>{label}</Text>
      <Badge data-testid={`operations-filter-tab-${label}`} isRounded>
        {voyagesByStatus}
      </Badge>
    </FilterBarTab>
  );
};

const VoyageFilterTabList: (props: {
  labels: string[];
  currentValue: VoyageStatus | null;
  tabValues: VoyageStatus[] | null;
  setActive: (x: VoyageStatus | null) => void;
  voyagesByStatusFilter: Map<VoyageStatus, TimelineVoyage[]>;
}) => JSX.Element = ({
  labels,
  currentValue,
  tabValues,
  setActive,
  voyagesByStatusFilter,
}) => {
  const all = [...voyagesByStatusFilter.values()].reduce(
    (acc, x) => [...acc, ...x],
    [],
  );
  const index: number =
    currentValue !== null
      ? (tabValues || []).indexOf(currentValue)
      : (tabValues || []).length - 1;

  const getVoyageStatusByIndex = (i: number) => {
    return (tabValues || [])[i] !== null && i !== labels.length - 1
      ? (voyagesByStatusFilter.get((tabValues || [])[i])?.length ?? 0)
      : all.length;
  };

  return (
    <>
      <Media query="(min-width: 1350px)">
        <Box display="flex" flexDirection="row">
          {labels.map((label: string, i: number) => (
            <VoyageFilterTab
              key={label}
              label={label}
              tabValue={(tabValues || [])[i]}
              currentValue={currentValue}
              setActive={setActive}
              voyagesByStatus={getVoyageStatusByIndex(i)}
            />
          ))}
        </Box>
      </Media>
      <Media query="(max-width: 1349px)">
        <DropdownButton
          label={`${labels[index]} - ${getVoyageStatusByIndex(index)}`}
          variant="secondary"
        >
          <Menu hasShadow>
            {labels.map((v, i) => {
              return (
                <MenuItem
                  key={v}
                  label={`${v} - ${getVoyageStatusByIndex(i)}`}
                  onPress={() => {
                    setActive((tabValues || [])[i]);
                  }}
                />
              );
            })}
          </Menu>
        </DropdownButton>
      </Media>
    </>
  );
};

const VoyagesTable = ({
  timelines,
  scheduleContext: { vessels, locations, portCalls },
  organizations,
  onViewAssignment,
  relatedOrgsData,
}: VoyagesTableProps): JSX.Element => {
  const relatedOrgsById = useMemo(() => {
    return new Map(
      relatedOrgsData?.allOrders?.nodes.map((o) => [o?.id, o?.relatedOrgs]),
    );
  }, [relatedOrgsData]);
  const allPortCallDbIds = useMemo<number[]>(
    () =>
      timelines
        .flatMap((timeline) =>
          timeline.voyageGroups.flatMap((vg) =>
            vg.portCalls.map((pc) => pc.id),
          ),
        )
        .map((clientId) => clientId && portCalls.get(clientId)?.id)
        .filter(isDefined),
    [portCalls, timelines],
  );

  const allOrderIds = useMemo(
    () =>
      timelines.flatMap((timeline) =>
        timeline.voyageGroups.flatMap((vg) => vg.orders.map((o) => o.id)),
      ),
    [timelines],
  );

  const { data: bolData, loading: bolLoading } =
    useVoyagesTable_BillOfLadingsQuery({
      variables: { portCallIds: allPortCallDbIds },
      skip: allPortCallDbIds.length === 0,
    });
  const { data: mrData, loading: mrLoading } =
    useVoyagesTable_MeasurementReportsQuery({
      variables: { orderIds: allOrderIds },
      skip: allPortCallDbIds.length === 0,
    });

  const portCallDataLoading = bolLoading || mrLoading;

  const bolByPortCallId = useMemo(
    () =>
      mapKeyBy(
        bolData?.allBillOfLadings?.nodes.filter(isDefined) ?? [],
        (bol) => bol.portCallId,
      ),
    [bolData],
  );
  const measurementsByOrderId = useMemo(
    () =>
      mapGroupBy(
        mrData?.allCargoMeasurements?.nodes.filter(isDefined) ?? [],
        (cm) => cm.orderId ?? NaN,
      ),
    [mrData],
  );

  const allVoyages = useMemo(() => timelines.flatMap(getVoyages), [timelines]);
  const dockEventsByPortCall = useMemo(
    () =>
      mapGroupBy(
        allVoyages.flatMap((v) =>
          v.timeline.voyageGroups.flatMap((vg) =>
            vg.events.filter(isPortCallEventTimeData),
          ),
        ),
        (td) => td.event.portCallId,
      ),
    [allVoyages],
  );

  const [voyageKeyword, setVoyageKeyword] = useLocalSetting(
    'voyageKeyword',
    '',
  );

  const [filters, setFilters] = useState<GlobalFilter>({
    search: voyageKeyword,
    fromDate: null,
    toDate: null,
  });

  const data = useMemo<VoyageTableRowData[]>(() => {
    const result: VoyageTableRowData[] = [];

    for (const voyage of allVoyages) {
      const shipowner =
        organizations.get(
          (voyage.timeline.type === 'coa' || voyage.timeline.type === 'spot'
            ? voyage.timeline.ownerOrganizationId
            : voyage.timeline.vessel?.organizationId) ?? NaN,
        ) ?? null;

      const shipownerName = shipowner?.name ?? '';

      const filterKeywords = new Set<string>();
      const shipment = voyage.shipment;
      if (shipment) {
        const shipmentName = shipment.name ?? `Shipment #${shipment.id}`;
        filterKeywords.add(shipmentName.toLowerCase());
      }

      voyage.cargoEvents.forEach(({ event }) => {
        const receiverOrgId =
          event.order.orderDetailsByOrderId.nodes[0]?.receiverOrganisationId;
        const receiverOrgName = receiverOrgId
          ? (organizations.get(receiverOrgId)?.name ?? '')
          : '';

        [
          event.order.locationByLoadPortId?.name ?? '',
          event.order.locationByDischargePortId?.name ?? '',
          event.vessel?.name ?? '',
          organizations.get(
            vessels.get(event.vesselId ?? NaN)?.organizationId ?? NaN,
          )?.name ?? '',
          event.order.assortmentByAssortmentId?.name ?? '',
          getOrderDisplayName(event.order),
          event.order.id.toString() ?? '',
          event.order.shipmentId?.toString() ?? '',
          event.order.cargoAmount.toString(),
          event.order.cargoUnitByCargoUnitId?.name ?? '',
          organizations.get(event.order.chartererOrganizationId ?? NaN)?.name ??
            '',
          shipownerName,
          receiverOrgName,
        ].forEach((w) => filterKeywords.add(w.toLowerCase()));
      });

      const rows: VoyageTableRowData[] = [];

      voyage.cargoEvents.forEach((e) => {
        const { portCallId } = e.event;

        if (portCallId === null) {
          return;
        }

        const portcall = portCalls.get(portCallId);

        const portCallEvents = typedKeyBy<
          PortCallEventType,
          TimeData<PortCallEvent>
        >(dockEventsByPortCall.get(portCallId) ?? [], (td) => td.event.event);

        const orgIds =
          relatedOrgsById.get(e.event.order.id)?.filter(isDefined) ?? [];
        const isReadOnly = !orgIds.some((orgId) =>
          hasUserRight(orgId, 'createAndEditPortCalls'),
        );

        rows.push({
          voyage,
          filterKeywords: Array.from(filterKeywords),
          shipownerName,
          event: e.event,
          portcall: portcall ?? null,
          allCargoEvents: voyage.cargoEvents,
          allPortCallEvents: portCallEvents,
          subRows: [],
          expanded: true,
          isReadOnly,
        });
      });

      if (rows.length > 0) {
        rows[0].subRows = rows.slice(1);
        rows[0].subRows.sort((a, b) => a.event.start - b.event.start);
        result.push(rows[0]);
      }
    }
    return result;
  }, [
    allVoyages,
    organizations,
    vessels,
    portCalls,
    dockEventsByPortCall,
    relatedOrgsById,
  ]);

  const voyageStatusFilter = useMemo(
    () =>
      (
        rows: Array<Row<VoyageTableRowData>>,
        columnIds: Array<string>,
        filterValue: unknown,
      ) => {
        return rows.filter(
          (row) =>
            filterValue == null || row.values[columnIds[0]] === filterValue,
        );
      },
    [],
  );

  const customSortType = React.useMemo(
    () =>
      (
        rowA: Row<VoyageTableRowData>,
        rowB: Row<VoyageTableRowData>,
        id: string,
        desc: boolean,
      ) => {
        // if subRows, we skip sorting
        if (!rowA.subRows.length && !rowB.subRows.length) {
          if (rowA?.values['pbs'] > rowB?.values['pbs']) return desc ? -1 : 1;
          if (rowB?.values['pbs'] > rowA?.values['pbs']) return desc ? 1 : -1;
          return desc ? -1 : 1;
        }

        if (rowA?.values[id] > rowB?.values[id]) return 1;
        if (rowB?.values[id] > rowA?.values[id]) return -1;
        return 0;
      },
    [],
  );

  const columns = React.useMemo<Array<Column<VoyageTableRowData>>>(
    () => [
      {
        Header: 'Voyage ID',
        accessor: (row) => {
          return row.voyage.shipment === null
            ? `Order #${row.event.order.id}`
            : row.voyage.shipment.name
              ? row.voyage.shipment.name
              : `Shipment #${row.voyage.shipment.id}`;
        },
        sortType: customSortType,
        id: 'voyageId',
      },
      {
        Header: 'Order',
        accessor: (row) =>
          row.event.order.orderDetailsByOrderId.nodes[0]?.orderName ??
          row.event.order.id,
        id: 'orderName',
        disableSortBy: true,
      },
      {
        Header: 'Charterer',
        accessor: (row) => {
          const orgId = row.event.order?.chartererOrganizationId ?? NaN;
          return organizations.get(orgId)?.name ?? null;
        },
        id: 'chartererOrganizationId',
        disableSortBy: true,
      },
      {
        Header: 'Vessel',
        accessor: (row) => {
          return row.event.vessel?.name ?? '';
        },
        id: 'vesselName',
        disableSortBy: true,
      },
      {
        Header: 'Shipowner',
        accessor: (row) => {
          return row.shipownerName;
        },
        id: 'shipownerName',
        disableSortBy: true,
      },
      {
        Header: 'Port',
        accessor: (row) => getEventLocation(row.event, locations).name ?? 'N/A',
        id: 'portName',
        disableSortBy: true,
      },
      {
        Header: 'Activity',
        accessor: (row) =>
          row.event.type === CoreEventType.Load ? 'Loading' : 'Discharging',
        id: 'eventType',
        disableSortBy: true,
      },
      {
        Header: 'Order ID',
        accessor: (row) => row.event.order.id,
        id: 'orderId',
        disableSortBy: true,
      },
      {
        Header: 'Commodity',
        accessor: (row) =>
          row.event.order.assortmentByAssortmentId?.name ?? 'N/A',
        id: 'assortmentName',
        disableSortBy: true,
      },
      {
        Header: 'Quantity',
        accessor: (row) => {
          return parseAmountInfo(row.event.order);
        },
        id: 'amount',
        disableSortBy: true,
      },
      {
        Header: 'Receiver',
        accessor: (row) => {
          const receiverOrgId =
            row.event.order.orderDetailsByOrderId.nodes[0]
              ?.receiverOrganisationId;
          return receiverOrgId
            ? (organizations.get(receiverOrgId)?.name ?? null)
            : null;
        },
        id: 'receiver',
        disableSortBy: true,
      },
      {
        Header: 'ETA/ATA PBS',
        Cell: ({
          row,
          value,
        }: {
          row: Row<VoyageTableRowData>;
          value: number | null;
        }) => {
          return portCallTimeCellContent(
            PortCallEventType.Arrival,
            value,
            row.original,
          );
        },
        accessor: (row) => {
          if (!row.portcall) return null;
          return (
            row.allPortCallEvents[PortCallEventType.Arrival]?.start ??
            row.allCargoEvents[0].start
          );
        },
        filter: (
          rows: Row<VoyageTableRowData>[],
          _columnIds: string[],
          {
            fromDate = null,
            toDate = null,
          }: { fromDate: Date | null; toDate: Date | null },
        ): Row<VoyageTableRowData>[] => {
          if (fromDate === null && toDate === null) return rows;

          const from = fromDate?.getTime() ?? null;
          const to = toDate ? new Date(toDate).setHours(23, 59, 59, 999) : null;

          const isInFilterRange = (time: number) =>
            (from ? time >= from : true) && (to ? time <= to : true);

          const rowsInRange: Row<VoyageTableRowData>[] = [];

          for (const row of rows) {
            const mainRowInRange = isInFilterRange(row.values.pbs);

            const subRowsInRange = row.subRows.filter((subRow) => {
              return isInFilterRange(subRow.values.pbs);
            });

            if (mainRowInRange || subRowsInRange.length > 0) {
              rowsInRange.push({
                ...row,
                subRows: mainRowInRange ? row.subRows : subRowsInRange,
              });
            }
          }

          return rowsInRange;
        },
        sortType: customSortType,
        id: 'pbs',
      },
      {
        Header: 'ETA/ATA Berth',
        Cell: ({
          row,
          value,
        }: {
          row: Row<VoyageTableRowData>;
          value: number | null;
        }) => {
          return portCallTimeCellContent(
            PortCallEventType.Berthing,
            value,
            row.original,
          );
        },
        accessor: (row) => {
          if (!row.portcall) return null;
          return (
            row.allPortCallEvents[PortCallEventType.Berthing]?.start ??
            row.allPortCallEvents[PortCallEventType.Arrival]?.start ??
            row.allCargoEvents[0].arriveInPort
          );
        },
        sortType: customSortType,
        id: 'berth',
      },
      {
        Header: 'ETD/ATD',
        Cell: ({
          row,
          value,
        }: {
          row: Row<VoyageTableRowData>;
          value: number | null;
        }) => {
          return portCallTimeCellContent(
            PortCallEventType.Departure,
            value,
            row.original,
          );
        },
        accessor: (row) => {
          if (!row.portcall) return null;
          return (
            row.allPortCallEvents[PortCallEventType.Departure]?.start ??
            row.allCargoEvents[row.allCargoEvents.length - 1].end
          );
        },
        sortType: customSortType,
        id: 'departure',
      },
      {
        Header: 'B/L weight',
        accessor: (row) => {
          if (row.isReadOnly) return <div />;
          if (!row.portcall)
            return <span style={{ color: 'orange' }}>NO PORT CALL</span>;
          const pc = row.portcall;
          const bol = bolByPortCallId.get(pc.id);
          const bolData = bol?.billOfLadingDataByBillOfLadingId.nodes.find(
            (bd) => bd?.orderId === row.event.order.id,
          );
          if (bolData?.weight != null) {
            return `${bolData.weight} ${
              getMassUnitName(bolData.weightUnit)?.symbol ?? ''
            }`;
          } else {
            return null;
          }
        },
        id: 'bolWeight',
      },
      {
        Header: 'B/L volume',
        accessor: (row) => {
          if (row.isReadOnly) return <div />;
          if (!row.portcall)
            return <span style={{ color: 'orange' }}>NO PORT CALL</span>;
          const pc = row.portcall;
          const bol = bolByPortCallId.get(pc.id);
          const bolData = bol?.billOfLadingDataByBillOfLadingId.nodes.find(
            (bd) => bd?.orderId === row.event.order.id,
          );
          if (bolData?.volume != null) {
            return `${bolData.volume} ${
              getVolumeUnitName(bolData.volumeUnit)?.symbol ?? ''
            }`;
          } else {
            return null;
          }
        },
        id: 'bolVolume',
      },
      {
        Header: 'Port call data',
        accessor: (row) => {
          if (row.isReadOnly) return <div />;
          if (!row.portcall)
            return <span style={{ color: 'orange' }}>NO PORT CALL</span>;
          const pc = row.portcall;
          const pn = pc.portNoticesByPortCallId.nodes[0];
          const sof = pc.statementOfFactsByPortCallId.nodes[0];
          const bol = bolByPortCallId.get(pc.id);
          const bolDatas = bol?.billOfLadingDataByBillOfLadingId.nodes;

          const orders = uniqBy(
            row.allCargoEvents.map((e) => e.event.order),
            (o) => o.id,
          );
          const measurements = orders.flatMap(
            (o) =>
              measurementsByOrderId
                .get(o.id)
                ?.filter((mr) => mr?.locationId === o.dischargePortId) ?? [],
          );

          const dataWithStatus = [
            [
              'Prospects',
              !!pn?.eta || !!pn?.berthingProspects || !!pn?.etdBerth,
            ],
            ['NOR', !!pn?.norTendered || !!pn?.ataPbp || !!pn?.ataBerth],
            [
              'SOF',
              !!sof?.arrivalTime ||
                !!sof?.pilotOnboard ||
                !!sof?.berthed ||
                !!sof?.noticeOfReadiness ||
                !!sof?.sailingTime,
            ],
            [
              'B/L',
              row.event.type === CoreEventType.Discharge
                ? undefined
                : !!bolDatas?.find(
                    (b) =>
                      b?.orderId === row.event.order.id &&
                      (!!b?.weight || !!b?.volume),
                  ),
            ],
            [
              'MR',
              row.event.type === CoreEventType.Load
                ? undefined
                : !!measurements?.find(
                    (m) =>
                      m?.orderId === row.event.order.id &&
                      (!!m?.measuredVolume || !!m?.measuredWeight),
                  ),
            ],
          ] as const;

          return (
            <Box
              display="flex"
              flexDirection="row"
              style={{ textDecoration: 'underline', cursor: 'pointer' }}
              onClick={(event) => {
                const { voyage } = row;
                const cellData =
                  (event.target as HTMLElement).textContent || '';

                const shipmentId = voyage.shipment?.clientId;
                const orderId = voyage.orderId;
                const id = isDefined(shipmentId)
                  ? { shipmentIds: [shipmentId] }
                  : orderId != null
                    ? { orderIds: [orderId] }
                    : null;

                if (id === null) {
                  console.error(`No id found for voyage: ${voyage}`);
                  return;
                }

                onViewAssignment({
                  timelineId: voyage.timeline.id,
                  ...id,
                  navTarget: {
                    tabName: 'documents',
                    portLocation: pc.locationByLocationId?.name || '',
                    section: cellData,
                  },
                });
              }}
            >
              {dataWithStatus.map(([key, isPresent]) => (
                <Box
                  marginRight={2}
                  key={key}
                  color={isPresent ? undefined : 'neutral.gray_390'}
                  fontWeight={isPresent ? 600 : undefined}
                >
                  {key}
                </Box>
              ))}
            </Box>
          );
        },
        id: 'portCallData',
        disableSortBy: true,
      },
      {
        Header: 'Remarks',
        accessor: (row) => {
          return (
            <Text maxWidth={200}>
              {row.event.order.orderDetailsByOrderId.nodes[0]?.extraInfo ?? ''}
            </Text>
          );
        },
        disableSortBy: true,
      },
      {
        Header: 'Actions',
        accessor: (row) => {
          if (row.isReadOnly) return <div />;
          return (
            <View
              flexDirection="row"
              alignSelf="flex-start"
              justifyContent="flex-end"
            >
              <Button
                variant="secondary"
                onPress={() =>
                  onViewAssignment({
                    timelineId: row.voyage.timeline.id,
                    ...(row.voyage.orderId !== null
                      ? { orderIds: [row.voyage.orderId] }
                      : {
                          shipmentIds: [row.voyage.shipment.clientId],
                        }),
                  })
                }
              >
                View
              </Button>
            </View>
          );
        },
        id: 'actions',
        disableSortBy: true,
      },
      {
        Header: 'Status',
        accessor: (row) => getVoyageStatus(row.voyage, portCalls),
        id: 'voyageStatus',
        Filter: () => null,
        filter: voyageStatusFilter,
        disableSortBy: true,
      },
    ],
    [
      customSortType,
      onViewAssignment,
      portCalls,
      voyageStatusFilter,
      bolByPortCallId,
      measurementsByOrderId,
      locations,
      organizations,
    ],
  );

  const [selectedStatusFilter, setSelectedStatusFilter] =
    useState<VoyageStatus | null>(VoyageStatus.ONGOING);
  const [] = useState<[Date | undefined, Date | undefined]>([
    undefined,
    undefined,
  ]);

  const voyagesByStatusFilter: Map<VoyageStatus, TimelineVoyage[]> =
    useMemo(() => {
      const filterWords = filters.search?.toLowerCase().split(/\s+/) || [];
      const filteredRows = data
        .filter((row: VoyageTableRowData) => {
          return filterWords.every((word: string) => {
            return row.filterKeywords.some((val: string) => val.includes(word));
          });
        })
        .map((x) => x.voyage);
      return mapGroupBy(filteredRows, (v) => getVoyageStatus(v, portCalls));
    }, [portCalls, data, filters.search]);

  const initialFilters = useMemo(
    () => [
      {
        id: 'voyageStatus',
        value: selectedStatusFilter,
      },
    ],
    [selectedStatusFilter],
  );

  const globalFilter = useCallback(
    (
      rows: Array<Row<VoyageTableRowData>>,
      _columnIds: Array<string>,
      filterValue: GlobalFilter['search'],
    ): Array<Row<VoyageTableRowData>> => {
      const filterWords = (filterValue ?? '')
        .split(/\s+/)
        .map((w) => w.toLowerCase());
      return rows.filter((row) =>
        filterWords.every((word) =>
          row.original.filterKeywords.some((val) => val.includes(word)),
        ),
      );
    },
    [],
  );

  // saved operation search
  const [savedKeywords, setSavedkeywords] = useLocalSetting(
    'voyagesSearchKeywords',
    [],
  );

  const handleSearchSave = (v: GlobalFilter) => {
    if (v.search && !savedKeywords.includes(v.search)) {
      setSavedkeywords([v.search, ...savedKeywords].slice(0, 10));
    }
  };

  const [hiddenColumns, saveHiddenColumns] = useLocalSetting(
    'voyagesTableHiddenColumns',
    ['voyageStatus'],
  );

  const [sortBy, saveSortBy] = useLocalSetting('voyagesTableSortBy', [
    { id: 'voyageId', desc: false },
  ]);

  const {
    allColumns,
    getTableProps,
    getTableBodyProps,
    getToggleHideAllColumnsProps,
    headerGroups,
    rows,
    prepareRow,
    setGlobalFilter,
    setFilter,
    state: tableState,
  } = useTable<VoyageTableRowData>(
    {
      columns,
      data,
      useControlledState: (state) => {
        return state;
      },
      initialState: {
        sortBy,
        filters: initialFilters,
        hiddenColumns,
      },
      globalFilter,
      autoResetGlobalFilter: false,
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    useExpanded,
  );

  useEffect(() => {
    saveHiddenColumns(tableState.hiddenColumns);
  }, [tableState.hiddenColumns, saveHiddenColumns]);

  useEffect(() => {
    saveSortBy(tableState.sortBy);
  }, [tableState.sortBy, saveSortBy]);

  useEffect(() => {
    setGlobalFilter(filters.search);
  }, [setGlobalFilter, filters.search]);

  useEffect(() => {
    setFilter('voyageStatus', selectedStatusFilter);
  }, [selectedStatusFilter, setFilter]);

  useEffect(() => {
    setFilter('pbs', filters);
  }, [filters, setFilter]);

  let previousMuted = true;

  return (
    <>
      <FilterBar overflowY="visible">
        <View flexDirection="row" alignItems="center">
          <VoyageFilterTabList
            labels={['Ongoing', 'Upcoming', 'Completed', 'All']}
            tabValues={[
              VoyageStatus.ONGOING,
              VoyageStatus.UPCOMING,
              VoyageStatus.COMPLETED,
            ]}
            currentValue={selectedStatusFilter}
            setActive={setSelectedStatusFilter}
            voyagesByStatusFilter={voyagesByStatusFilter}
          />
        </View>
        <View flexGrow={1} mx="3" />
        {portCallDataLoading && (
          <View flexDirection="row" mr={4}>
            <LoadingSpinner />
            <Text color="neutral.white_50" ml={3} fontWeight="bold">
              Loading port call data
            </Text>
          </View>
        )}
        <FilterBarDateRangePicker
          label="Date Range"
          mr={3}
          datePickerProps={{
            onChange: (v) => {
              if (!v) setFilters({ ...filters, fromDate: null, toDate: null });
              else {
                const [fromDate, toDate] = v;
                setFilters({ ...filters, fromDate, toDate });
              }
            },
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          }}
        />

        <SearchFilter
          savedKeywords={savedKeywords}
          globalFilter={filters}
          setGlobalFilter={(v) => {
            setVoyageKeyword(v.search || '');
            setFilters(v);
          }}
          handleSearchSave={(v) => handleSearchSave(v)}
        />

        <FilterBarSeparator />
        <TableColumnsSelector
          allColumns={allColumns}
          getToggleHideAllColumnsProps={getToggleHideAllColumnsProps}
        />
      </FilterBar>
      <View height="calc(100vh - 50px - 48px - 48px)">
        <Resizable
          enable={{
            bottom: true,
          }}
          defaultSize={{
            width: '100%',
            height: '100%',
          }}
          lockAspectRatio={false}
        >
          <View overflowY="auto" height="100%" width="100%">
            <Table
              {...getTableProps()}
              height={rows.length ? 'auto' : '100%'}
              data-testid="voyage-operations-table"
            >
              <thead>
                {headerGroups.map((headerGroup) => (
                  // eslint-disable-next-line react/jsx-key
                  <TableHeaders isSticky {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      // eslint-disable-next-line react/jsx-key
                      <TableHeader
                        {...column.getHeaderProps(
                          column.getSortByToggleProps(),
                        )}
                      >
                        {column.render('Header')}{' '}
                        {column.isSorted
                          ? column.isSortedDesc
                            ? ' ▼'
                            : ' ▲'
                          : ''}
                      </TableHeader>
                    ))}
                  </TableHeaders>
                ))}
              </thead>
              <tbody {...getTableBodyProps}>
                {rows.length ? (
                  rows.map((row, index) => {
                    const cellsToHide: string[] = [];
                    const prev: VoyageTableRowData | undefined =
                      index > 0 ? rows[index - 1]?.original : undefined;
                    const curr = row.original;

                    let keepRowColor = false;
                    if (prev) {
                      const prevIsSameOrder =
                        curr.event.order.id === prev.event.order.id;

                      const prevIsSameVoyage = curr.subRows.length === 0;

                      const prevIsSamePort =
                        index > 0 &&
                        curr.event.portCallId === prev.event.portCallId;

                      if (prevIsSameOrder) {
                        cellsToHide.push('orderName');
                      }

                      if (prevIsSameVoyage) {
                        keepRowColor = true;

                        cellsToHide.push(
                          'voyageId',
                          'vesselName',
                          'shipownerName',
                        );

                        if (prevIsSamePort) {
                          cellsToHide.push(
                            'berth',
                            'departure',
                            'documents',
                            'pbs',
                            'portCallData',
                            'portName',
                          );
                        }
                      }
                    }

                    const thisMuted = keepRowColor
                      ? previousMuted
                      : !previousMuted;
                    previousMuted = thisMuted;
                    prepareRow(row);

                    return (
                      // eslint-disable-next-line react/jsx-key
                      <VoyagesTableRow
                        row={row}
                        index={index}
                        isMuted={thisMuted}
                        isActive={false}
                        hiddenCells={cellsToHide}
                        onDblClick={(event, row) => {
                          const { original } = row;
                          const cellData =
                            (event.target as HTMLElement).textContent || '';

                          onViewAssignment({
                            timelineId: original.voyage.timeline.id,
                            ...(original.voyage.orderId !== null
                              ? { orderIds: [original.voyage.orderId] }
                              : {
                                  shipmentIds: [
                                    original.voyage.shipment.clientId,
                                  ],
                                }),
                            navTarget: {
                              tabName: 'documents',
                              portLocation: cellData,
                            },
                          });
                        }}
                        onSelect={() => null}
                        {...row.getRowProps()}
                      />
                    );
                  })
                ) : (
                  <NotFoundTableRow
                    colSpan={columns.length}
                    isSearching={
                      !!allVoyages.length &&
                      Object.values(filters).some((x) => !!x)
                    }
                  />
                )}
              </tbody>
            </Table>
          </View>
        </Resizable>
      </View>
    </>
  );
};

export default dynamic(() => Promise.resolve(VoyagesTable), {
  ssr: false,
});
